import { useCallback, useEffect, useRef, useState } from 'react';

interface PronunciationSelection {
  text: string;
  left: number;
  top: number;
}

/**
 * Prepares the floating speech button from the browser's native selection.
 * Desktop: the reading area's double-click handler schedules a selection read.
 * Touch: long-press lets the browser choose the word; selectionchange reads it
 * and follows any later selection-handle adjustments. We never split the text
 * into characters or override the browser's word boundaries here.
 * This hook only prepares the button; HomeworkPreview starts speech on click.
 */
export function usePronunciationSelection(enabled: boolean) {
  // Track the latest input method, including on devices with both touch and mouse.
  const touchSelection = useRef(false);
  const frame = useRef<number>();
  const [selection, setSelection] = useState<PronunciationSelection>();
  const clearSelection = useCallback(() => setSelection(undefined), []);
  const updateSelection = useCallback(() => {
    if (
      !enabled ||
      !('speechSynthesis' in window) ||
      !('SpeechSynthesisUtterance' in window)
    ) {
      clearSelection();
      return;
    }
    // Read the native highlighted text, not the element that received the tap.
    const selected = window.getSelection();
    if (!selected || selected.isCollapsed || !selected.rangeCount) {
      clearSelection();
      return;
    }
    // The entire range must belong to one marked reading/question container.
    // This excludes page controls, unrelated text, and selections across sections.
    const range = selected.getRangeAt(0);
    const ancestor = range.commonAncestorContainer;
    const element =
      ancestor instanceof Element ? ancestor : ancestor.parentElement;
    const container = element?.closest('[data-pronunciation-text]');
    // Accept Han text with punctuation/spacing; reject Latin/pinyin or mixed text.
    const text = selected.toString().trim();
    if (
      !container?.isConnected ||
      !container.contains(ancestor) ||
      !/\p{Script=Han}/u.test(text) ||
      /[^\p{Script=Han}\p{P}\p{Z}\s]/u.test(text)
    ) {
      clearSelection();
      return;
    }
    // Position the fixed speaker below the selection, clamped to the viewport.
    const rect = range.getBoundingClientRect();
    if (!rect.width && !rect.height) {
      clearSelection();
      return;
    }
    setSelection({
      text,
      left: Math.min(
        Math.max(rect.left + rect.width / 2, 24),
        window.innerWidth - 24,
      ),
      top: Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 48)),
    });
  }, [enabled, clearSelection]);

  const cancelFrame = useCallback(() => {
    if (frame.current !== undefined) window.cancelAnimationFrame(frame.current);
    frame.current = undefined;
  }, []);
  // Let the browser finish selecting its word before reading the range.
  // Coalesce rapid selection changes into one read per animation frame.
  const scheduleSelection = useCallback(() => {
    cancelFrame();
    frame.current = window.requestAnimationFrame(() => {
      frame.current = undefined;
      updateSelection();
    });
  }, [cancelFrame, updateSelection]);

  useEffect(() => {
    touchSelection.current = false;
    if (
      !enabled ||
      !('speechSynthesis' in window) ||
      !('SpeechSynthesisUtterance' in window)
    ) {
      clearSelection();
      return;
    }
    const dismiss = () => {
      cancelFrame();
      clearSelection();
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      const element = target instanceof Element ? target : null;
      // Keep the prepared selection alive while the speaker receives a tap.
      if (element?.closest('[data-speech-button]')) return;
      touchSelection.current = event.pointerType === 'touch';
      dismiss();
    };
    const handleSelectionChange = () => {
      // Only touch selection activates the assist here. Desktop activation
      // comes exclusively from the reading area's double-click handler.
      if (touchSelection.current) scheduleSelection();
      else clearSelection();
    };
    // Keyboard selection must not inherit activation from an earlier touch.
    const handleKeyDown = () => {
      touchSelection.current = false;
      cancelFrame();
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    // Hide stale coordinates when the page or a nested scroll container moves.
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      // Disabling speech or leaving the page removes listeners, pending work,
      // and any utterance that is still playing.
      cancelFrame();
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
      window.speechSynthesis.cancel();
    };
  }, [enabled, scheduleSelection, cancelFrame, clearSelection]);

  return { selection, clearSelection, onDoubleClick: scheduleSelection };
}
